import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';

export function resolveProgramTitle(content: any): string {
  return content?.parent?.title || '';
}

export function resolveContentImage(content: any): string | undefined {
  if (typeof content?.image === 'string') {
    return content.image;
  }

  return (
    content?.image?.scales?.large?.download ||
    content?.image?.scales?.preview?.download ||
    content?.image?.scales?.teaser?.download ||
    content?.image?.download
  );
}

export function resolveProgramImage(content: any): string | undefined {
  const scaled =
    content?.parent?.image_scales?.image?.[0]?.scales?.preview?.download ||
    content?.parent?.image_scales?.image?.[0]?.scales?.large?.download ||
    content?.parent?.image_scales?.image?.[0]?.scales?.teaser?.download ||
    content?.parent?.image_scales?.image?.[0]?.download;

  return scaled ? `${content?.parent?.['@id']}/${scaled}` : undefined;
}

export function resolveHeroVisual(content: any): string | undefined {
  return resolveContentImage(content) || resolveProgramImage(content);
}

function resolveAudioField(value: any): string | undefined {
  if (!value) return undefined;

  if (typeof value === 'string') {
    return value.trim() || undefined;
  }

  return value?.download || value?.url || undefined;
}

export function resolveAudioUrl(content: any): string | undefined {
  return (
    resolveAudioField(content?.audio_link) || resolveAudioField(content?.audio)
  );
}

export function resolveContentKindToken(content: any): string {
  return content?.audio_type?.token || '';
}

export function resolveContentKindLabel(content: any): string {
  return content?.audio_type?.title || content?.type_title || 'Conteúdo';
}

export function isPodcast(content: any): boolean {
  return resolveContentKindToken(content) === 'podcast';
}

export function isEpisode(content: any): boolean {
  return resolveContentKindToken(content) === 'episodio';
}

export function isCall(content: any): boolean {
  return resolveContentKindToken(content) === 'chamada';
}

export function shouldShowListenButton(content: any): boolean {
  return !!resolveAudioUrl(content);
}

export function shouldShowDownloadButton(content: any): boolean {
  return isPodcast(content) && !!resolveAudioUrl(content);
}

export function resolveAiringDate(content: any): string | null {
  return content?.date_airing || null;
}

export function resolveCreatedDate(content: any): string | null {
  return content?.created || null;
}

export function resolveModifiedDate(content: any): string | null {
  return content?.modified || null;
}

export function resolveEffectiveDate(content: any): string | null {
  return content?.effective || null;
}

export function resolvePublishedDate(content: any): string | null {
  return content?.effective || content?.created || null;
}

export function shouldShowUpdatedDate(content: any): boolean {
  if (!content?.created || !content?.modified) return false;
  return content.created !== content.modified;
}

export function resolveTags(content: any): string[] {
  if (Array.isArray(content?.subjects)) return content.subjects;
  if (Array.isArray(content?.Subject)) return content.Subject;
  return [];
}

export function formatDate(value?: string | null): string {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function resolvePageUrl(content: any): string {
  return flattenToAppURL(content?.['@id'] || '') || '';
}

export function resolvePublicPageUrl(content: any): string {
  const raw = content?.['@id'] || '';
  if (!raw) return '';

  return toPublicURL(raw);
}

function getSlateNodeText(node: any): string {
  if (!node) return '';

  if (typeof node.text === 'string') {
    return node.text;
  }

  if (Array.isArray(node.children)) {
    return node.children.map(getSlateNodeText).join(' ');
  }

  return '';
}

function hasUsefulSlateValue(value: any): boolean {
  if (!Array.isArray(value)) return false;

  const text = value
    .map(getSlateNodeText)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > 0;
}

function isBlockUseful(block: any): boolean {
  if (!block || block['@type'] === 'title') return false;

  if (block['@type'] === 'slate') {
    return hasUsefulSlateValue(block?.value);
  }

  return true;
}

export function getRenderableBlockIds(content: any): string[] {
  const items = content?.blocks_layout?.items || [];
  const blocks = content?.blocks || {};

  return items.filter((id: string) => isBlockUseful(blocks[id]));
}

export function hasRenderableBlocks(content: any): boolean {
  return getRenderableBlockIds(content).length > 0;
}

export function buildBlocksContentWithoutTitle(content: any) {
  const ids = getRenderableBlockIds(content);

  if (!ids.length) return null;

  const nextBlocks = ids.reduce((acc: Record<string, any>, id: string) => {
    acc[id] = content.blocks[id];
    return acc;
  }, {});

  return {
    ...content,
    blocks: nextBlocks,
    blocks_layout: {
      ...content?.blocks_layout,
      items: ids,
    },
  };
}

export function hasUsefulBody(content: any): boolean {
  const body = content?.body;

  if (!body || typeof body !== 'string') return false;

  const cleaned = body
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned.length > 0;
}

export function hasContentBody(content: any): boolean {
  return hasRenderableBlocks(content) || hasUsefulBody(content);
}

export function resolveReferenceDate(content: any): {
  value: string;
  field: 'date_airing' | 'effective';
} | null {
  if (content?.date_airing) {
    return {
      value: content.date_airing,
      field: 'date_airing',
    };
  }

  if (content?.effective) {
    return {
      value: content.effective,
      field: 'effective',
    };
  }

  return null;
}

export function resolveImageCredit(content: any): string {
  const credit = content?.image_credit;

  if (typeof credit !== 'string') return '';

  return credit.trim();
}
